/**
 * Retry wrapper utility for async functions
 * 
 * Provides a higher-order function pattern that wraps any async function with
 * exponential backoff retry logic. Useful for network operations, API calls,
 * and database operations that may experience transient failures.
 */

import { RETRY_CONFIG, calculateBackoffDelay, shouldRetry } from './RetryConfig.js';
import errorHandler from '../handlers/ErrorHandler.js';

/**
 * Wrap an async function with exponential backoff retry logic
 * 
 * Useful for wrapping operations that may fail transiently (network issues,
 * rate limiting, service unavailability) but should succeed on retry.
 * 
 * The wrapper automatically:
 * - Retries on transient errors up to maxRetries times
 * - Uses exponential backoff between retries
 * - Fails fast on permanent errors (permissions, not found)
 * - Logs retry attempts for debugging
 * 
 * @param {Function} fn - Async function to wrap with retry logic
 * @param {string} operationName - Name of the operation for logging
 * @param {Object} [config=RETRY_CONFIG] - Retry configuration
 * @returns {Function} Wrapped async function that handles retries
 * 
 * @example
 * // Wrap a single function
 * const getDocWithRetry = withRetry(
 *     (collection, id) => FirestoreClient.getDocument(collection, id),
 *     'FirestoreClient.getDocument'
 * );
 * 
 * // Use it transparently with the same parameters
 * const doc = await getDocWithRetry('products', 'pejas');
 * 
 * @example
 * // Wrap a method that doesn't take parameters
 * const signInWithRetry = withRetry(
 *     () => AuthClient.signInAnonymously(),
 *     'AuthClient.signInAnonymously'
 * );
 * 
 * const userId = await signInWithRetry();
 */
export function withRetry(fn, operationName, config = RETRY_CONFIG) {
    return async (...args) => {
        let lastError;
        
        for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
            try {
                return await fn(...args);
            } catch (error) {
                lastError = error;
                
                // Don't retry on final attempt - just throw
                if (attempt === config.maxRetries) {
                    break;
                }
                
                // Check if this error should be retried
                if (!shouldRetry(error, config)) {
                    // Permanent error - fail fast
                    throw error;
                }
                
                // Transient error - calculate delay and retry
                const delay = calculateBackoffDelay(attempt, config);
                errorHandler.handle(error, { 
                    operation: operationName,
                    attempt: attempt + 1,
                    maxRetries: config.maxRetries,
                    delay,
                    errorCode: error.code
                }, 'warning');
                
                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
        
        // All retries exhausted - throw the last error
        throw lastError;
    };
}

export default withRetry;
